import { ROUTE_PPRA_MEMBERSHIP_INTERSTITIAL, ROUTE_PPRA_QUESTIONNAIRE } from '@/constants/routes';
import useOnMount from '@/hooks/useOnMount';
import { useSetIntercomIsVisible } from '@/queries/layout.queries';
import { useFeatureFlags, VariantFlags } from '@/services/feature-flag-provider';
import { Flex } from '@cmpsr/components';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BaseLoadingScreen from '../common/BaseLoadingScreen';
import { SocialProofMarquee } from '../social-proof/components';
import LoadingMessage from './components/LoadingMessage';

/**
 * Loading screen shown before the PPRA questionnaire.
 * This is a standalone route that navigates to the questionnaire after loading.
 */
const PPRALoadingScreen: React.FC = () => {
  const navigate = useNavigate();
  const setIntercomIsVisible = useSetIntercomIsVisible();
  const { getVariant, isLoading } = useFeatureFlags();
  const [nextRoute, setNextRoute] = useState<string>(ROUTE_PPRA_QUESTIONNAIRE);

  // This has 5 seconds to check the feature flag
  useEffect(() => {
    if (!isLoading) {
      const isPpraMembershipInterstitialEnabled =
        getVariant(VariantFlags.IsPpraMembershipInterstitialEnabled) === 'treatment';
      if (isPpraMembershipInterstitialEnabled) {
        setNextRoute(ROUTE_PPRA_MEMBERSHIP_INTERSTITIAL);
      }
    }
  }, [isLoading, getVariant]);

  useOnMount(() => {
    setIntercomIsVisible.mutate(false);
    return () => {
      setIntercomIsVisible.mutate(true);
    };
  });

  const componentsAbove = [<LoadingMessage key="loading-message" />];
  const componentsBelow = [
    <Flex key="social-proof-1" position="relative" zIndex="1" flexDirection="column" gap="spacer-6">
      <SocialProofMarquee key="social-proof-1" direction="left" />
      <SocialProofMarquee key="social-proof-2" direction="right" />
    </Flex>,
  ];

  const handleLoadingComplete = () => {
    if (nextRoute) {
      navigate(nextRoute, { replace: true });
    }
  };

  return (
    <BaseLoadingScreen
      variant="light"
      navBarVariant="white"
      backButtonVariant="dark"
      componentsAboveProgressBar={componentsAbove}
      componentsBelowProgressBar={componentsBelow}
      minimumLoadingSeconds={4}
      onLoadingComplete={handleLoadingComplete}
      screenId="ppra-questionnaire-loading"
    />
  );
};

export default PPRALoadingScreen;
